// src/pages/TasksPage.js
import React, { useEffect, useState } from "react";
import UnifiedPageWrapper from "../components/Layout/UnifiedPageWrapper";
import SmartTable from "../components/Layout/SmartTable";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  HourglassBottom as HourglassIcon,
  PendingActions as TodoIcon,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DoneIcon from "@mui/icons-material/Done";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { useGlobalCounts } from "../context/GlobalCountContext";
import { useToast } from "../context/GlobalToastContext";
import { tSuccess, tError, tInfo } from "../utils/toast";
import ConfirmDialog from "../components/ConfirmDialog";
import useAuth from "../hooks/useAuth";
import CrmDialog from "../components/CrmDialog";
import AddTaskModal from "../components/AddTaskModal";
import ModalFooter from "../components/ModalFooter";





export default function TasksPage() {
  const navigate = useNavigate();
  const { can } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]); // for assigning reps
  const [selectedTask, setSelectedTask] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { refreshCounts } = useGlobalCounts();
  const { notifySuccess, notifyError } = useToast();
  const [openAdd, setOpenAdd] = useState(false);
  const [loading, setLoading] = useState(true);


  // ------------------------------
  // FETCH TASKS & USERS
  // ------------------------------
  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await API.get("/tasks/");
      await refreshCounts();
      setTasks(
  res.data.map(task => {
    const rep = users.find(u => u.id === task.assigned_to);

    return {
      ...task,
      assigned_to_display:
        rep?.username ||
        task.assigned_to_username ||
        task.assigned_to_name ||
        (task.assigned_to ? `User #${task.assigned_to}` : "—"),
    };
  })
);

    } catch (err) {
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await API.get("/users/");
      await refreshCounts();
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  // ------------------------------
  // TABLE CONFIG
  // ------------------------------
  const columns = [

  { field: "title", headerName: "Title", flex: 1.4 },
  { field: "description", headerName: "Description", flex: 1.8 },

  // Assigned Rep
  {
    field: "assigned_to",
    headerName: "Assigned Rep",
    flex: 1,
    valueGetter: (params) => {
      if (!params?.row) return "—";
      const { assigned_to_display, assigned_to_username, assigned_to_name, assigned_to } = params.row || {};
      return assigned_to_display || assigned_to_username || assigned_to_name || (assigned_to ? `User #${assigned_to}` : "—");
    },
    renderCell: (params) => {
      if (!params?.row) return "—";
      const { assigned_to_username, assigned_to_name, assigned_to } = params.row || {};
      return assigned_to_username || assigned_to_name || (assigned_to ? `User #${assigned_to}` : "—");
    },
  },

  // Due Date
  {
    field: "due_date",
    headerName: "Due Date",
    flex: 0.8,
    valueGetter: (params) => {
      if (!params?.row) return "—";
      const { due_date } = params.row || {};
      if (!due_date) return "—";
      const d = new Date(due_date);
      return isNaN(d) ? due_date : d.toLocaleDateString();
    },
    renderCell: (params) => {
      if (!params?.row) return "—";
      const { due_date } = params.row || {};
      if (!due_date) return "—";
      const d = new Date(due_date);
      return isNaN(d) ? due_date : d.toLocaleDateString();
    },
  },
  

{
  field: "priority",
  headerName: "Priority",
  flex: 0.6,
  sortable: true,
  valueGetter: (params) => {
    if (!params?.row) return "—";
    const p = params.row.priority || "";
    if (!p) return "—";
    return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
  },
  renderCell: (params) => {
    if (!params?.row) return "—";
    const p = (params.row.priority || "").toLowerCase();

    if (p === "low") {
      return (
        <span
          style={{
            color: "gray",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          low
        </span>
      );
    }

    if (p === "medium") {
      return (
        <span
          style={{
            color: "#FB8C00",
            fontWeight: 500,
            fontSize: "16px",
          }}
        >
          ! 
        </span>
      );
    }

    if (p === "high") {
      return (
        <span
          style={{
            color: "#E65100",
            fontWeight: 500,
            fontSize: "16px",
          }}
        >
          !! 
        </span>
      );
    }

    return "—";
  },
},



  // Status
  {
  field: "status",
  headerName: "Status",
  flex: 1,
  valueGetter: (params) => {
    if (!params?.row) return "—";
    const status = params.row.status || "";
    if (!status) return "—";
    const s = status.toLowerCase();
    if (s === "completed") return "Completed";
    if (s === "in progress") return "In Progress";
    if (s === "to do" || s === "todo") return "To Do";
    return status;
  },
  renderCell: (params) => {
    if (!params?.row) return "—";
    const status = params.row.status || "";

    if (status.toLowerCase() === "completed") {
      return (
        <Chip
          icon={<DoneIcon sx={{ color: "green !important" }} />}
          label="Completed"
          sx={{
            color: "grey",
            backgroundColor: "rgba(158, 158, 158, 0.12)",
            fontWeight: 600,
	    borderRadius: "6px",
          }}
        />
      );
    }

    if (status.toLowerCase() === "in progress") {
      return (
        <Chip
          icon={<AutorenewIcon sx={{ color: "#1b53cd !important" }} />}
          label="In Progress"
          sx={{
            color: "#1b53cd",
            backgroundColor: "rgba(158, 158, 158, 0.12)",
            fontWeight: 600,
	    borderRadius: "6px",
          }}
        />
      );
    }

    if (status.toLowerCase() === "to do" || status.toLowerCase() === "todo") {
      return (
        <Chip
          icon={<TodoIcon sx={{ color: "#B01E1E !important" }} />}
          label="To Do"
          sx={{
            color: "#b01e1e",
            backgroundColor: "rgba(158, 158, 158, 0.12)",
            fontWeight: 600,
	    borderRadius: "6px",
          }}
        />
      );
    }

    return (
      <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
        {status || "—"}
      </Box>
    );
  },
},
];


  // ------------------------------
  // EDIT TASK LOGIC
  // ------------------------------
  const handleRowClick = (params) => {
    setSelectedTask(params.row);
    setOpenEdit(true);
  };

  const handleChange = (field, value) => {
    setSelectedTask((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveTask = async () => {
  try {
    console.log("📦 PUT payload:", selectedTask.status);
    console.log("📦 sending payload:", selectedTask);
    await API.put(`/tasks/${selectedTask.id}/`, selectedTask);
await refreshCounts();
 tSuccess("Task updated successfully!");
    setOpenEdit(false);
    setSelectedTask(null);
    await fetchTasks();                // refresh from backend
    console.log("✅ tasks refreshed after save");
  } catch (err) {
     tError("Error updating task.");
  }
};

    const handleTaskComplete = async (id) => {
  try {
    await API.patch(`/tasks/${id}/`, { status: "Done" });
    await fetchTasks();
    await refreshCounts();
    notifySuccess("Task completed successfully!");
  } catch (err) {
    notifyError("Error completing task.");
  }
};


// ------------------------------
// HANDLE DELETE TASK
// ------------------------------
const handleDeleteTask = () => {
  if (!selectedTask) return;
  setConfirmDelete(true);
};

const handleConfirmDelete = async () => {
  try {
    await API.delete(`/tasks/${selectedTask.id}/`);
    await refreshCounts();
    tSuccess("Task deleted successfully!");
    setOpenEdit(false);
    setSelectedTask(null);
    await fetchTasks(); // refresh list
  } catch (err) {
    console.error("Error deleting task:", err);
    tError("Failed to delete task.");
  }
};

  // ------------------------------
  // RENDER
  // ------------------------------
  return (
    <UnifiedPageWrapper
      title="Tasks"
      subtitle="Create, assign, and track tasks for your team members. Monitor progress and ensure timely completion of important activities."
      actionButton={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAdd(true)}
        >
          Add Task
        </Button>
      }
    >
      <Alert severity="warning" sx={{ mb: 2 }}>
        Deprecated. Tasks are an earlier version of the CRM and are no longer shown in dashboard activity. This page stays until we confirm nothing else still opens it, then it will be removed.
      </Alert>
      {loading ? (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="text" width="30%" sx={{ mb: 2 }} />
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : tasks.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary" gutterBottom>No tasks yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add a task to assign to a rep.</Typography>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)}>
            Add your first task
          </Button>
        </Box>
      ) : (
        <SmartTable
          rows={tasks}
          columns={columns}
          onRowClick={handleRowClick}
          mobileEssentialFields={[
    "title",
    "description",
    "assigned_to",  // Uses valueGetter to display assigned_to_display
    "due_date",
    "priority",
    "status"
  ]}
        />
      )}

     <AddTaskModal
  open={openAdd}
  onClose={() => setOpenAdd(false)}
  onCreated={fetchTasks}
/>


      {/* --------------------------------------------------------------------------------------🧩 Edit Task Modal */}
      <CrmDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
 	 PaperProps={{
    sx: {
      minHeight: "40vh", // ✅ gives the dialog a comfortable height
      maxHeight: "80vh", // keeps it from growing offscreen
      display: "flex",
      flexDirection: "column",
    },
  }}
      >
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent dividers>
          {selectedTask && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Title"
                value={selectedTask.title || ""}
                onChange={(e) => handleChange("title", e.target.value)}
                fullWidth
              />
              <TextField
                label="Description"
                value={selectedTask.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={3}
                fullWidth
              />

              {/* --------------------------------------------------------------Assigned Rep */}
              <TextField
  select
  fullWidth
  label="Assigned Rep"
  value={selectedTask.assigned_to || ""}
  onChange={(e) => handleChange("assigned_to", e.target.value)}
  InputLabelProps={{ shrink: true }}
>
  {users.map((user) => (
    <MenuItem key={user.id} value={user.id}>
      {user.username}
    </MenuItem>
  ))}
</TextField>


              {/* ----------------------------------------------------------------Due Date */}
              <TextField
                label="Due Date"
                type="date"
                value={
                  selectedTask.due_date
                    ? selectedTask.due_date.split("T")[0]
                    : ""
                }
                onChange={(e) => handleChange("due_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              {/* ---------------------------------------------------------------Status Selector */}
              <TextField
  select
  fullWidth
  label="Status"
  value={selectedTask.status || ""}
  onChange={(e) => handleChange("status", e.target.value)}
  InputLabelProps={{ shrink: true }}
>
  <MenuItem value="To Do">To Do</MenuItem>
  <MenuItem value="In Progress">In Progress</MenuItem>
  <MenuItem value="Completed">Completed</MenuItem>
</TextField>

            </Box>
          )}
        </DialogContent>
       <ModalFooter
  showDelete={!!selectedTask?.id && can.canDelete}
  deleteLabel="Delete Task"
  onDelete={handleDeleteTask}

  onCancel={() => setOpenEdit(false)}

  saveLabel="Update"
  onSave={handleSaveTask}
/>




      </CrmDialog>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
        itemName={selectedTask?.title ? `Task: ${selectedTask.title}` : null}
      />
    </UnifiedPageWrapper>
  );
}
